/* ============================================
   VELANTRA - Global JavaScript
   ============================================ */

// Mobile Navigation
class MobileNav {
  constructor() {
    this.toggle = document.querySelector('.header__menu-toggle');
    this.nav = document.querySelector('.mobile-nav');
    this.close = document.querySelector('.mobile-nav__close');
    if (this.toggle && this.nav) this.init();
  }

  init() {
    this.toggle.addEventListener('click', () => this.open());
    if (this.close) this.close.addEventListener('click', () => this.closeNav());
    this.nav.addEventListener('click', (e) => {
      if (e.target === this.nav) this.closeNav();
    });
  }

  open() {
    this.nav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeNav() {
    this.nav.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

// Product Accordion (toggle individual, allow multiple open)
class ProductAccordion {
  constructor() {
    this.items = document.querySelectorAll('.product-accordion__item');
    if (this.items.length === 0) return;
    this.init();
  }

  init() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('.product-accordion__trigger');
      if (trigger) {
        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          if (isOpen) {
            item.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      }
    });
  }
}

// Product Gallery — Thumbnail + Main Image (Cuyana-style)
class ProductGallery {
  constructor() {
    this.thumbsContainer = document.querySelector('[data-product-thumbs]');
    this.mainImage = document.getElementById('product-main-img');
    this.thumbs = this.thumbsContainer ? Array.from(this.thumbsContainer.querySelectorAll('.product-thumb')) : [];
    this.imagesData = [];

    try {
      const imagesJson = document.getElementById('product-images-json');
      if (imagesJson) this.imagesData = JSON.parse(imagesJson.textContent);
    } catch(e) {
      console.warn('Could not parse images data', e);
    }

    if (this.thumbs.length > 0 && this.mainImage) this.init();
  }

  init() {
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.thumbIndex);
        this.goToImage(index);
      });
    });
  }

  goToImage(index) {
    if (!this.imagesData[index]) return;
    const imgData = this.imagesData[index];

    // Update main image — must clear srcset so browser uses src
    this.mainImage.removeAttribute('srcset');
    this.mainImage.src = imgData.src_main;
    this.mainImage.alt = imgData.alt || '';

    // Update active thumb
    this.thumbs.forEach(t => t.classList.remove('active'));
    const activeThumb = this.thumbs.find(t => parseInt(t.dataset.thumbIndex) === index);
    if (activeThumb) {
      activeThumb.classList.add('active');
      // Scroll thumb into view if needed
      activeThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  // Called by VariantSelector to show first image of variant group
  goToFirstVisible() {
    const firstVisible = this.thumbs.find(t => !t.classList.contains('variant-hidden'));
    if (firstVisible) {
      const index = parseInt(firstVisible.dataset.thumbIndex);
      this.goToImage(index);
    }
  }

  // Filter thumbnails by variant
  filterByVariant(visibleImageIds, sharedImageIds) {
    const hasSpecific = visibleImageIds && visibleImageIds.size > 0;

    this.thumbs.forEach(thumb => {
      const imgId = parseInt(thumb.dataset.imageId);
      if (!hasSpecific) {
        thumb.classList.remove('variant-hidden');
      } else if (visibleImageIds.has(imgId) || (sharedImageIds && sharedImageIds.has(imgId))) {
        thumb.classList.remove('variant-hidden');
      } else {
        thumb.classList.add('variant-hidden');
      }
    });

    this.goToFirstVisible();
  }
}

// Mobile Carousel — simple scroll-snap with dot indicators
class MobileCarousel {
  constructor() {
    this.container = document.querySelector('[data-mobile-carousel]');
    if (!this.container) return;
    this.track = this.container.querySelector('.product-mobile-carousel__track');
    this.slides = Array.from(this.container.querySelectorAll('.product-mobile-carousel__slide'));
    this.dots = Array.from(this.container.querySelectorAll('.product-mobile-carousel__dot'));
    if (this.slides.length > 0) this.init();
  }

  init() {
    // Listen for scroll to update dots
    this.track.addEventListener('scroll', () => {
      const scrollLeft = this.track.scrollLeft;
      const slideWidth = this.track.offsetWidth;
      const visibleSlides = this.getVisibleSlides();
      let activeIdx = 0;
      visibleSlides.forEach((slide, i) => {
        if (Math.abs(slide.offsetLeft - scrollLeft) < slideWidth * 0.5) {
          activeIdx = i;
        }
      });
      this.updateDots(activeIdx);
    });
  }

  getVisibleSlides() {
    return this.slides.filter(s => !s.classList.contains('variant-hidden'));
  }

  updateDots(activeIdx) {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === activeIdx);
    });
  }

  filterByVariant(visibleImageIds, sharedImageIds) {
    const hasSpecific = visibleImageIds && visibleImageIds.size > 0;

    this.slides.forEach(slide => {
      const imgId = parseInt(slide.dataset.imageId);
      if (!hasSpecific) {
        slide.classList.remove('variant-hidden');
      } else if (visibleImageIds.has(imgId) || (sharedImageIds && sharedImageIds.has(imgId))) {
        slide.classList.remove('variant-hidden');
      } else {
        slide.classList.add('variant-hidden');
      }
    });

    // Rebuild dots
    this.rebuildDots();

    // Scroll to first visible
    const firstVisible = this.getVisibleSlides()[0];
    if (firstVisible) {
      this.track.scrollTo({ left: firstVisible.offsetLeft, behavior: 'instant' });
    }
  }

  rebuildDots() {
    const dotsContainer = this.container.querySelector('.product-mobile-carousel__dots');
    if (!dotsContainer) return;
    const visibleSlides = this.getVisibleSlides();
    dotsContainer.innerHTML = '';
    visibleSlides.forEach((_, i) => {
      const dot = document.createElement('span');
      dot.className = 'product-mobile-carousel__dot' + (i === 0 ? ' active' : '');
      dotsContainer.appendChild(dot);
    });
    this.dots = Array.from(dotsContainer.querySelectorAll('.product-mobile-carousel__dot'));
  }
}

// Cart Drawer with Upsells, Quantity Controls, and Remove
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer__overlay');
    this.openBtns = document.querySelectorAll('[data-cart-toggle]');
    this.closeBtn = document.querySelector('.cart-drawer__close');
    if (this.drawer) this.init();
  }

  init() {
    this.openBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    // Quantity controls
    this.drawer.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('.cart-drawer__qty-btn');
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        const action = qtyBtn.dataset.action;
        const qtyEl = qtyBtn.parentElement.querySelector('.cart-drawer__qty-value');
        let qty = parseInt(qtyEl.textContent);
        if (action === 'increase') qty++;
        if (action === 'decrease') qty = Math.max(0, qty - 1);
        this.updateItem(key, qty);
      }

      // Remove button
      const removeBtn = e.target.closest('.cart-drawer__remove-btn');
      if (removeBtn) {
        this.updateItem(removeBtn.dataset.key, 0);
      }

      // Upsell add-to-cart
      const upsellBtn = e.target.closest('.cart-upsell-card__add');
      if (upsellBtn && !upsellBtn.classList.contains('added')) {
        const variantId = upsellBtn.dataset.variantId;
        upsellBtn.textContent = 'Adding...';
        upsellBtn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
        })
        .then(r => r.json())
        .then(() => {
          upsellBtn.textContent = 'Added \u2713';
          upsellBtn.classList.add('added');
          this.refreshDrawer();
        })
        .catch(() => {
          upsellBtn.textContent = '+ Add to Bag';
          upsellBtn.disabled = false;
        });
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async updateItem(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      this.refreshDrawer();
    } catch (err) {
      console.error('Cart update failed:', err);
    }
  }

  async refreshDrawer() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      // Update header count
      const countEl = document.querySelector('.header__cart-count');
      if (countEl) {
        countEl.textContent = cart.item_count;
        countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }

      // Update drawer title
      const titleEl = this.drawer.querySelector('.cart-drawer__title');
      if (titleEl) titleEl.textContent = `Your Bag (${cart.item_count})`;

      // Update items
      const itemsContainer = this.drawer.querySelector('.cart-drawer__items');
      if (itemsContainer) {
        if (cart.items.length === 0) {
          itemsContainer.innerHTML = '<div class="cart-drawer__empty"><p>Your bag is empty.</p><a href="/collections/all" class="cart-drawer__continue-link">Continue Shopping</a></div>';
        } else {
          let html = '';
          cart.items.forEach(item => {
            const price = (item.final_line_price / 100).toFixed(2);
            const imgSrc = item.image ? item.image.replace(/(\.[^.]+)$/, '_200x$1') : '';
            const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : '';
            html += `
              <div class="cart-drawer__item">
                <a href="${item.url}" class="cart-drawer__item-image-link">
                  ${imgSrc ? `<img src="${imgSrc}" alt="${item.title}" class="cart-drawer__item-image" loading="lazy">` : ''}
                </a>
                <div class="cart-drawer__item-details">
                  <a href="${item.url}" class="cart-drawer__item-title">${item.product_title.replace('Velantra ', '')}</a>
                  ${variantTitle}
                  <p class="cart-drawer__item-price">$${price}</p>
                  <div class="cart-drawer__item-qty">
                    <button class="cart-drawer__qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                    <span class="cart-drawer__qty-value">${item.quantity}</span>
                    <button class="cart-drawer__qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                    <button class="cart-drawer__remove-btn" data-key="${item.key}" aria-label="Remove">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            `;
          });
          itemsContainer.innerHTML = html;
        }
      }

      // Update subtotal
      const subtotalEl = this.drawer.querySelector('.cart-drawer__subtotal-amount');
      if (subtotalEl) subtotalEl.textContent = `$${(cart.total_price / 100).toFixed(2)}`;

      // Free shipping on all orders — no threshold
      const shippingBar = this.drawer.querySelector('.cart-drawer__shipping-bar');
      if (shippingBar) {
        shippingBar.classList.add('cart-drawer__shipping-bar--complete');
        const msgEl = shippingBar.querySelector('.cart-drawer__shipping-msg');
        if (msgEl) msgEl.innerHTML = '<strong>Free shipping</strong> on your order ✨';
        const progressFill = shippingBar.querySelector('.cart-drawer__progress-fill');
        if (progressFill) progressFill.style.width = '100%';
      }

      // Show/hide footer
      const footer = this.drawer.querySelector('.cart-drawer__footer');
      if (footer) {
        footer.style.display = cart.item_count > 0 ? '' : 'none';
        if (cart.item_count > 0 && !footer.querySelector('.cart-drawer__checkout')) {
          const checkoutLink = document.createElement('a');
          checkoutLink.href = '/checkout';
          checkoutLink.className = 'cart-drawer__checkout';
          checkoutLink.textContent = 'Checkout';
          footer.appendChild(checkoutLink);
        }
      }

    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }
}

// Variant Selector with Image Filtering
class VariantSelector {
  constructor() {
    this.form = document.querySelector('.product-form');
    if (!this.form) return;

    this.swatches = document.querySelectorAll('.product-swatch');
    this.sizes = document.querySelectorAll('.product-size');
    this.priceEl = document.querySelector('[data-product-price]');
    this.variantSelect = this.form.querySelector('select[name="id"]');
    this.submitBtn = this.form.querySelector('[type="submit"]');

    this.variantsData = [];
    this.imagesData = [];
    try {
      const variantsJson = document.getElementById('product-variants-json');
      if (variantsJson) this.variantsData = JSON.parse(variantsJson.textContent);
      const imagesJson = document.getElementById('product-images-json');
      if (imagesJson) this.imagesData = JSON.parse(imagesJson.textContent);
    } catch(e) {
      console.warn('Could not parse variant/image data', e);
    }

    this.init();
  }

  init() {
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        const parentOptions = swatch.closest('.product-swatches__options') || swatch.parentElement;
        parentOptions.querySelectorAll('.product-swatch').forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
        const label = swatch.closest('.product-swatches').querySelector('.product-swatches__label strong');
        if (label) label.textContent = swatch.getAttribute('title') || swatch.dataset.optionValue;
        this.updateSizeImages(swatch.dataset.optionValue);
        this.updateVariant();
      });
    });

    this.sizes.forEach(size => {
      size.addEventListener('click', () => {
        const parentOptions = size.closest('.product-sizes__options') || size.parentElement;
        parentOptions.querySelectorAll('.product-size').forEach(s => s.classList.remove('active'));
        size.classList.add('active');
        const label = size.closest('.product-sizes').querySelector('.product-sizes__label strong');
        if (label) label.textContent = size.dataset.optionValue;
        this.updateVariant();
      });
    });

    // Apply initial size images for selected color
    const initialSwatch = document.querySelector('.product-swatch.active');
    if (initialSwatch) {
      this.updateSizeImages(initialSwatch.dataset.optionValue);
    }

    // Apply initial gallery filter
    this.filterGalleryByVariant();
  }

  updateVariant() {
    if (!this.variantSelect) return;

    const selectedOptions = [];
    const colorSwatch = document.querySelector('.product-swatch.active');
    if (colorSwatch) selectedOptions.push(colorSwatch.dataset.optionValue);
    const sizeBtn = document.querySelector('.product-size.active');
    if (sizeBtn) selectedOptions.push(sizeBtn.dataset.optionValue);

    let matchedVariant = null;
    for (const variant of this.variantsData) {
      const match = selectedOptions.every((sel, i) => variant.options[i] === sel);
      if (match) {
        matchedVariant = variant;
        break;
      }
    }

    if (matchedVariant) {
      this.variantSelect.value = matchedVariant.id;

      // Update price display (handles both simple and sale markup)
      if (this.priceEl) {
        const priceInDollars = (matchedVariant.price / 100).toFixed(2);
        const priceSpan = this.priceEl.querySelector('.product-info__price');
        if (priceSpan) {
          priceSpan.textContent = '$' + priceInDollars;
        } else {
          this.priceEl.textContent = '$' + priceInDollars;
        }
      }

      if (this.submitBtn) {
        if (!matchedVariant.available) {
          this.submitBtn.disabled = true;
          this.submitBtn.textContent = 'Sold Out';
        } else {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = 'Add to Bag';
        }
      }

      const url = new URL(window.location);
      url.searchParams.set('variant', matchedVariant.id);
      window.history.replaceState({}, '', url);

      // Directly update main image from variant's image data
      this.updateMainImageForVariant(matchedVariant);

      this.filterGalleryByVariant(matchedVariant);
    }
  }

  updateMainImageForVariant(variant) {
    if (!variant) return;
    const mainImg = document.getElementById('product-main-img');
    if (!mainImg) return;

    // Always clear srcset so browser respects the new src
    mainImg.removeAttribute('srcset');

    // Strategy 1: Find image by variant's image_id
    if (variant.image_id) {
      const imgData = this.imagesData.find(img => img.id === variant.image_id);
      if (imgData) {
        mainImg.src = imgData.src_main;
        mainImg.alt = imgData.alt || '';
        return;
      }
    }

    // Strategy 2: Find image by variant_ids mapping in images data
    const imgByVariant = this.imagesData.find(img =>
      img.variant_ids && img.variant_ids.includes(variant.id)
    );
    if (imgByVariant) {
      mainImg.src = imgByVariant.src_main;
      mainImg.alt = imgByVariant.alt || '';
      return;
    }

    // Strategy 3: Use the swatch thumbnail to derive a high-res URL
    const activeSwatch = document.querySelector('.product-swatch.active');
    if (activeSwatch) {
      const swatchImg = activeSwatch.querySelector('img');
      if (swatchImg) {
        // Shopify CDN URLs use ?width=N — swap to 1200 for high-res
        let mainSrc = swatchImg.src;
        if (mainSrc.includes('width=')) {
          mainSrc = mainSrc.replace(/width=\d+/, 'width=1200');
        } else {
          mainSrc = mainSrc + (mainSrc.includes('?') ? '&' : '?') + 'width=1200';
        }
        mainImg.src = mainSrc;
      }
    }
  }

  updateSizeImages(colorName) {
    const sizeButtons = document.querySelectorAll('.product-size--image');
    sizeButtons.forEach(btn => {
      const sizeImagesData = btn.dataset.sizeImages;
      if (!sizeImagesData) return;
      try {
        const sizeImages = JSON.parse(sizeImagesData);
        const imgUrl = sizeImages[colorName];
        if (imgUrl) {
          const img = btn.querySelector('img');
          if (img) img.src = imgUrl;
        }
      } catch(e) {
        console.warn('Could not parse size images data', e);
      }
    });
  }

  filterGalleryByVariant(variant) {
    if (!variant) {
      const selectedId = this.variantSelect ? this.variantSelect.value : null;
      if (selectedId) {
        variant = this.variantsData.find(v => v.id == selectedId);
      }
    }
    if (!variant) return;

    const variantId = variant.id;
    const variantImageId = variant.image_id;

    // Find ALL variant IDs that share the same Color option
    const colorOption = variant.options[0];
    const sameColorVariantIds = new Set();
    this.variantsData.forEach(v => {
      if (v.options[0] === colorOption) {
        sameColorVariantIds.add(v.id);
      }
    });

    // Build set of image IDs for this color group
    const variantImageIds = new Set();
    if (variantImageId) variantImageIds.add(variantImageId);

    this.variantsData.forEach(v => {
      if (sameColorVariantIds.has(v.id) && v.image_id) {
        variantImageIds.add(v.image_id);
      }
    });

    // Match by variant_ids on the image
    this.imagesData.forEach(img => {
      if (img.variant_ids && img.variant_ids.length > 0) {
        const hasMatch = img.variant_ids.some(vid => sameColorVariantIds.has(vid));
        if (hasMatch) {
          variantImageIds.add(img.id);
        }
      }
    });

    // Match by alt text — images whose alt matches the color option name
    this.imagesData.forEach(img => {
      if (img.alt && img.alt === colorOption) {
        variantImageIds.add(img.id);
      }
    });

    // Filter desktop gallery (thumbnails + main image)
    if (window.velantraGallery) {
      window.velantraGallery.filterByVariant(variantImageIds, null);
    }

    // Filter mobile carousel
    if (window.velantraMobileCarousel) {
      window.velantraMobileCarousel.filterByVariant(variantImageIds, null);
    }
  }
}


// Lazy Video Loading — only download when scrolled into view
class LazyVideo {
  constructor() {
    this.videos = document.querySelectorAll('[data-lazy-video]');
    if (this.videos.length === 0) return;
    this.init();
  }

  init() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const video = entry.target;
          const source = video.querySelector('source[data-src]');
          if (source) {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
            video.load();
            video.play().catch(() => {});
          }
          observer.unobserve(video);
        }
      });
    }, { rootMargin: '300px' });

    this.videos.forEach(v => observer.observe(v));
  }
}

// Video Play/Pause Toggle
class VideoToggle {
  constructor() {
    this.buttons = document.querySelectorAll('.product-video-section__play-btn');
    if (this.buttons.length === 0) return;
    this.init();
  }

  init() {
    this.buttons.forEach(btn => {
      const video = btn.closest('.product-video-section__media').querySelector('video');
      if (!video) return;

      btn.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          btn.classList.remove('is-paused');
        } else {
          video.pause();
          btn.classList.add('is-paused');
        }
      });
    });
  }
}

// Scroll Animations
class ScrollAnimations {
  constructor() {
    this.elements = document.querySelectorAll('[data-animate]');
    if (this.elements.length > 0) this.init();
  }

  init() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    this.elements.forEach(el => observer.observe(el));

    // Fallback: reveal any elements already scrolled past (e.g. fast scroll/jump)
    const revealPassed = () => {
      this.elements.forEach(el => {
        if (!el.classList.contains('animate-in') && el.getBoundingClientRect().bottom < window.innerHeight) {
          el.classList.add('animate-in');
        }
      });
    };
    window.addEventListener('scroll', revealPassed, { passive: true });
    // Run once on load in case page loaded scrolled
    revealPassed();
  }
}

// Add to Cart (AJAX)
class AddToCart {
  constructor() {
    this.forms = document.querySelectorAll('.product-form');
    this.init();
  }

  init() {
    this.forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn.disabled) return;
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Adding...';
        submitBtn.disabled = true;

        try {
          const variantSelect = form.querySelector('select[name="id"]');
          const variantId = variantSelect ? variantSelect.value : null;

          if (!variantId) {
            submitBtn.textContent = 'Error - No variant';
            setTimeout(() => { submitBtn.textContent = originalText; submitBtn.disabled = false; }, 2000);
            return;
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: parseInt(variantId),
              quantity: 1
            })
          });

          if (response.ok) {
            submitBtn.textContent = 'Added!';
            if (window.velantraCartDrawer) {
              await window.velantraCartDrawer.refreshDrawer();
              window.velantraCartDrawer.open();
            }
          } else {
            const errorData = await response.json().catch(() => null);
            console.error('Add to cart error:', errorData);
            submitBtn.textContent = 'Error';
          }
        } catch (err) {
          console.error('Add to cart exception:', err);
          submitBtn.textContent = 'Error';
        }

        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      });
    });
  }
}

// Initialize everything on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new MobileNav();
  new ProductAccordion();
  window.velantraGallery = new ProductGallery();
  window.velantraMobileCarousel = new MobileCarousel();
  window.velantraCartDrawer = new CartDrawer();
  new VariantSelector();
  new LazyVideo();
  new VideoToggle();
  new ScrollAnimations();
  new AddToCart();
});


